import { useMemo, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react';
import type { CreateProjectRequest, ProjectDetail, ProjectListResponse } from '../../shared/api-types.js';
import { PROJECT_NAME_MAX } from '../../shared/projects.js';
import { api } from '../api.js';
import { Button, Checkbox, Fieldset, inlineFailureText, ProjectChip, StateView, TextField, useRequest } from '../ui/index.js';

// Filing by hand (design §7.4): a small composition, not a new primitive — the existing Fieldset and
// Checkbox (03-components §3.2, §3.3) with one row per own live project, each label a ProjectChip, so
// the colour and the word arrive together; 44px rows under a coarse pointer and keyboard-operable by
// the native control. "New project" adds one inline through POST /api/projects and ticks it. The
// choice is applied on save by the dialog that holds this (a checkbox, not a toggle, §3.2).

export interface ProjectOption {
  id: string;
  name: string;
  slot?: number | null;
}

/** The person's own live projects, A to Z — the list this composition loads when none is supplied. */
export const OWN_PROJECTS_PATH = '/api/projects?kind=own&sort=name&dir=asc';

export const NO_PROJECTS_SENTENCE = 'No projects yet.';
/** Why "Add" waits (03-components §2; app-baseline 05-client §5.5): nothing has been typed to add. */
export const NEW_PROJECT_NAME_FIRST_REASON = 'Name the new project first.';
export const NO_PROJECTS_ADD_SENTENCE = 'No projects yet. Add one below and it is ticked for you.';

export interface ProjectCheckboxesProps {
  /**
   * The projects to offer. When omitted the component loads the person's own live projects itself
   * (`OWN_PROJECTS_PATH`); a parent that already holds them passes them to spare the request.
   */
  options?: readonly ProjectOption[] | null;
  /** The ticked project ids. */
  selected: readonly string[];
  onChange: (next: string[]) => void;
  legend?: ReactNode;
  description?: ReactNode;
  /** Offer the inline "New project" add (default true). */
  allowCreate?: boolean;
  /** After an inline add: the created project, already ticked through `onChange`. */
  onCreated?: (project: ProjectDetail) => void;
  disabled?: boolean;
  className?: string;
}

/** Deduplicated by id and ordered by name, case-insensitively, so an added project lands where a reader looks for it. */
export function orderProjectOptions(groups: ReadonlyArray<readonly ProjectOption[]>): ProjectOption[] {
  const seen = new Set<string>();
  const all: ProjectOption[] = [];
  for (const group of groups) {
    for (const option of group) {
      if (seen.has(option.id)) continue;
      seen.add(option.id);
      all.push(option);
    }
  }
  return all.sort((a, b) => a.name.localeCompare(b.name, 'en', { sensitivity: 'base' }));
}

export function ProjectCheckboxes({ options, selected, onChange, legend = 'Projects', description, allowCreate = true, onCreated, disabled = false, className }: ProjectCheckboxesProps) {
  const supplied = options !== undefined && options !== null;
  // Why a resolved result when the list is supplied: hooks cannot be conditional, and a request that
  // never runs is simpler than a second code path.
  const own = useRequest<ProjectListResponse>(
    (signal) => (supplied
      ? Promise.resolve({ ok: true as const, status: 200, data: { items: [], total: 0, matching: 0, backfill: 'none' as const, sort: { state: 'off' as const, cooldownUntil: null, job: null, last: null, paths: [], lastOutcome: null } } })
      : api.get<ProjectListResponse>(OWN_PROJECTS_PATH, { signal })),
    [supplied],
  );
  const [added, setAdded] = useState<ProjectOption[]>([]);
  const [name, setName] = useState('');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [announced, setAnnounced] = useState('');

  const base: readonly ProjectOption[] | null = supplied ? options : own.data?.items ?? null;
  const list = useMemo(() => orderProjectOptions([base ?? [], added]), [base, added]);

  const toggle = (id: string, checked: boolean): void => {
    const without = selected.filter((other) => other !== id);
    onChange(checked ? [...without, id] : without);
  };

  const add = async (): Promise<void> => {
    const trimmed = name.trim();
    if (trimmed === '' || adding) return;
    setAdding(true);
    setAddError(null);
    const result = await api.post<ProjectDetail>('/api/projects', { name: trimmed } satisfies CreateProjectRequest);
    setAdding(false);
    if (!result.ok) {
      // The server's own sentence: "X already exists" (409, naming the holder) or why the name was refused (400).
      setAddError(inlineFailureText(result));
      return;
    }
    const created = result.data;
    setAdded((current) => [...current, { id: created.id, name: created.name, slot: created.slot }]);
    onChange([...selected.filter((other) => other !== created.id), created.id]);
    onCreated?.(created);
    setName('');
    setAnnounced(`Added ${created.name}`);
  };

  const onNameKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>): void => {
    // Why: inside a dialog's form, Enter in this field adds the project; it must not submit the dialog.
    if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
      event.preventDefault();
      void add();
    }
  };

  // Why the add waits for the list: the dialog holding this lands focus on the body's first control (03-components §4.1),
  // and a "New project" field standing alone while the projects load would be that control — focus would start past
  // every checkbox the dialog exists to tick. With nothing focusable until the list is there, the Dialog lands on its
  // title and moves to the first checkbox when it arrives (`arrivedFocusTarget`). A failed load still offers the add
  // beneath its Retry: a project can be made without the list.
  const loading = base === null && own.status !== 'error';

  let body: ReactNode;
  if (base === null) {
    body = own.status === 'error'
      ? <StateView kind="error" compact title="We couldn't load your projects" description={own.error?.message} onRetry={own.reload} correlationId={own.error?.correlationId} />
      : <StateView kind="loading" compact label="Loading your projects" minHeight="2.5rem" />;
  } else if (list.length === 0) {
    body = <p className="project-checkboxes__empty">{allowCreate ? NO_PROJECTS_ADD_SENTENCE : NO_PROJECTS_SENTENCE}</p>;
  } else {
    body = (
      <div className="project-checkboxes__list">
        {list.map((project) => (
          <Checkbox
            key={project.id}
            className="project-checkboxes__row"
            label={<ProjectChip project={project} link={false} />}
            checked={selected.includes(project.id)}
            onChange={(event) => toggle(project.id, event.target.checked)}
            disabled={disabled}
          />
        ))}
      </div>
    );
  }

  return (
    <Fieldset legend={legend} description={description} className={['project-checkboxes', className].filter(Boolean).join(' ')}>
      {body}
      {allowCreate && !loading ? (
        <div className="project-checkboxes__add">
          <TextField
            label="New project"
            value={name}
            onChange={(event) => {
              setName(event.target.value);
              if (addError) setAddError(null);
            }}
            onKeyDown={onNameKeyDown}
            maxLength={PROJECT_NAME_MAX}
            autoComplete="off"
            spellCheck={false}
            placeholder="e.g. Alpine Logistics"
            error={addError}
            disabled={disabled || adding}
            className="project-checkboxes__name"
          />
          {/* Why a reason only for the empty name (app-baseline 05-client §5.5; Phase 1c D16): that wait is the reader's to
              end, by keyboard as by pointer; `disabled` is the holding dialog's save in flight, its loading state (AB-Q7). */}
          <Button variant="secondary" icon="plus" onClick={() => void add()} loading={adding} disabled={disabled || name.trim() === ''} disabledReason={!disabled && name.trim() === '' ? NEW_PROJECT_NAME_FIRST_REASON : undefined}>Add</Button>
        </div>
      ) : null}
      <p className="visually-hidden" role="status">{announced}</p>
    </Fieldset>
  );
}
